import type { Store } from '@/data';
import { DocumentData, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

export const getStore = async (storeId: string): Promise<Store | undefined> => {
  try {
    const docRef = doc(db, 'stores', storeId);
    const docSnap = await getDoc(docRef);
    const data = docSnap.exists() ? docSnap.data() : null;
    const id = docSnap.id;
    if (!data) {
      console.warn('Retrieved empty data for store:', id);
      return;
    }

    const store = mapToStore(id, data);

    // Filter out archived stores
    if (store.status === 'archived') {
      return;
    }

    return store;
  } catch (error) {
    console.error('Error fetching data:', error);
    return;
  }
};

export const getStoreDashboardUrl = async (storeId: string, dashboardId: string): Promise<string | undefined> => {
  try {
    const docRef = doc(db, 'stores', storeId);
    const docSnap = await getDoc(docRef);
    const storeConfig = docSnap.data();
    if (!storeConfig) {
      console.warn('Retrieved empty dashboard data for store:', storeId);
      return undefined;
    }
    return `${process.env.REACT_APP_DASHBOARD_BASE_URL}/${storeConfig.client_id}/${dashboardId}`;
  } catch (error) {
    console.error('Error fetching data:', error);
    return;
  }
};

export const fetchStore = (
  storeId: string,
  onDataUpdate: (store: Store | null) => void,
  onError: (error: unknown) => void,
) => {
  const docRef = doc(db, 'stores', storeId);

  return onSnapshot(
    docRef,
    async (docSnap) => {
      try {
        const data = docSnap.exists() ? docSnap.data() : undefined;

        if (data) {
          const storeData = mapToStore(storeId, data);

          // Filter out archived stores
          if (storeData.status === 'archived') {
            onDataUpdate(null);
            return;
          }

          onDataUpdate(storeData);
        } else {
          onDataUpdate(null);
        }
      } catch (error) {
        onError(error);
      }
    },
    onError,
  );
};

export const mapToStore = (id: string, data: DocumentData): Store => {
  return {
    id: id,
    name: data.name,
    client_id: data.client_id,
    owner: data.owner,
    chain: data.chain,
    personOfContact: data.person_of_contact,
    status: data.status,
  };
};
